import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Clock, CalendarDays,
  Banknote, Receipt, Target, UserPlus, GraduationCap,
  MessageSquare, BarChart3, Shield, Settings, UserCog,
  LogOut, X, ChevronRight, Package, ClipboardCheck,
  HeartPulse, ShieldAlert, FileText, CreditCard,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { navForRole, ROLE_META } from '../../utils/roles';
import api from '../../utils/api';

const ICONS = {
  LayoutDashboard, Users, Building2, Clock, CalendarDays,
  Banknote, Receipt, Target, UserPlus, GraduationCap,
  MessageSquare, BarChart3, Shield, Settings, UserCog,
  Package, ClipboardCheck, HeartPulse, ShieldAlert, FileText, CreditCard,
};

function NavItem({ item, onClose, depth = 0, counts = {} }) {
  const [isOpen, setIsOpen] = React.useState(true);
  const Icon = ICONS[item.icon];
  const hasChildren = item.children && item.children.length > 0;

  const getCombinedCount = (itm) => {
    if (itm.children && itm.children.length > 0) {
      return itm.children.reduce((sum, child) => sum + getCombinedCount(child), 0);
    }
    return counts[itm.href] || 0;
  };
  const count = getCombinedCount(item);

  if (hasChildren) {
    return (
      <div style={{ marginBottom: 2 }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '9px 12px', borderRadius: 8,
            fontSize: 13.5, color: 'var(--sidebar-text)',
            background: 'transparent', border: 'none', cursor: 'pointer',
            transition: 'all 0.12s ease', textAlign: 'left',
          }}
          onMouseOver={e => e.currentTarget.style.background = 'var(--sidebar-hover)'}
          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
        >
          {Icon && <Icon size={15} style={{ opacity: 0.65, flexShrink: 0 }} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
          {count > 0 && (
            <span style={{
              background: '#DC2626',
              color: '#FFFFFF',
              fontSize: '11px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '10px',
              minWidth: '16px',
              textAlign: 'center',
              lineHeight: 1,
              display: 'inline-block',
              marginRight: 6
            }}>
              {count}
            </span>
          )}
          <ChevronRight size={13} style={{ opacity: 0.5, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
        {isOpen && (
          <div style={{ marginLeft: 12, marginTop: 2, borderLeft: '1px solid var(--sidebar-border)' }}>
            {item.children.map(child => (
              <NavItem key={child.label} item={child} onClose={onClose} depth={depth + 1} counts={counts} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      onClick={onClose}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 8, marginBottom: 2,
        fontSize: depth > 0 ? 12.5 : 13.5, fontWeight: isActive ? 500 : 400,
        color: isActive ? 'var(--sidebar-active)' : 'var(--sidebar-text)',
        background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
        textDecoration: 'none', transition: 'all 0.12s ease',
        position: 'relative',
        marginLeft: depth > 0 ? 12 : 0,
      })}
      onMouseOver={e => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.background = 'var(--sidebar-hover)';
        }
      }}
      onMouseOut={e => {
        if (!e.currentTarget.getAttribute('aria-current')) {
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && depth === 0 && (
            <div style={{
              position: 'absolute', left: 0, top: '50%',
              transform: 'translateY(-50%)',
              width: 3, height: 16, borderRadius: '0 3px 3px 0',
              background: 'var(--sidebar-active)',
            }} />
          )}
          {Icon && <Icon size={15} style={{ opacity: isActive ? 1 : 0.65, flexShrink: 0 }} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
          {count > 0 && (
            <span style={{
              background: '#DC2626',
              color: '#FFFFFF',
              fontSize: '11px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '10px',
              minWidth: '16px',
              textAlign: 'center',
              lineHeight: 1,
              display: 'inline-block',
              marginRight: 6
            }}>
              {count}
            </span>
          )}
          {isActive && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--sidebar-active)', opacity: 0.8 }} />}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = React.useState({});

  React.useEffect(() => {
    if (!user) return;

    const fetchCounts = async () => {
      try {
        const res = await api.get('/notifications/sidebar-counts');
        setCounts(res.data.data || res.data || {});
      } catch (err) {
        console.error('Failed to fetch sidebar counts:', err);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [user]);

  if (!user) return null;

  const navItems = navForRole(user.role, user);
  const meta = ROLE_META[user.role] || { label: user.role, color: '#6C63FF' };
  const initials = user.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <>
      <div
        className={`sidebar-backdrop ${open ? 'show' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`app-sidebar ${open ? 'open' : ''}`}>
        <div style={{
          padding: '12px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--sidebar-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img 
              src="/logo.png" 
              alt="Linqsoft Logo" 
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }} 
            />
          </div>
          <button className="btn btn-icon" onClick={onClose} style={{ display: 'none', color: 'var(--sidebar-muted)', background: 'none', border: 'none' }} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--sidebar-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `${meta.color}28`, border: `1px solid ${meta.color}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: meta.color,
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.88)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.full_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--sidebar-muted)', marginTop: 1 }}>
                {meta.label}
              </div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          {navItems.map(item => (
            <NavItem key={item.label} item={item} onClose={onClose} counts={counts} />
          ))}
        </nav>

        <div style={{ padding: '10px', borderTop: '1px solid var(--sidebar-border)' }}>
          <button
            onClick={logout}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '9px 12px', borderRadius: 8,
              fontSize: 13.5, color: 'rgba(252,110,110,0.75)',
              background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.12s',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <LogOut size={15} style={{ opacity: 0.8 }} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
